// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList from "@insite/content-library/Components/CardList";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import { StyledButton } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const recentWishListsParameter: GetWishListsApiParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    pageSize: 10,
    expand: ["top3products"],
};

const mapStateToProps = (state: ApplicationState) => ({
    recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
    myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
    canViewWishLists: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    gridContainer?: GridContainerProps;
    spinnerGridItem?: GridItemProps;
    wishListCardGridItem?: GridItemProps;
    notify?: TypographyPresentationProps;
    loadMoreGridItem?: GridItemProps;
    loadMoreLink?: LinkPresentationProps;
}

export const recentWishListsStyles: RecentWishListsStyles = {};

const styles = recentWishListsStyles;

const NavWishLists: FC<Props> = ({ canViewWishLists, loadWishLists, myListsPageNavLink, recentWishListsDataView }) => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (canViewWishLists && !recentWishListsDataView.value && !recentWishListsDataView.isLoading) {
            setIsLoading(true);
            loadWishLists({
                ...recentWishListsParameter,
                onComplete: result => {
                    setIsLoading(false);
                },
            });
        }
    }, [recentWishListsDataView.value]);

    if (isLoading) {
        return (
            <CardList data-test-selector="cardListRecentWishLists" {...styles.gridContainer}>
                <GridItem {...styles.spinnerGridItem}>
                    <LoadingSpinner size={32} />
                </GridItem>
            </CardList>
        );
    }

    if (!recentWishListsDataView.value || !canViewWishLists || recentWishListsDataView.value.length === 0) {
        return (
            <CardList data-test-selector="cardListRecentWishLists" {...styles.gridContainer}>
                <GridItem width={11}>
                    <Notify {...styles.notify}>{translate("No Lists Found.")}</Notify>
                </GridItem>
            </CardList>
        );
    }

    return (
        <CardList data-test-selector="cardListRecentWishLists" {...styles.gridContainer}>
            {recentWishListsDataView.value?.map(wishList => {
                if (wishList.isHidden) {
                    return null;
                }

                return (
                    <GridItem
                        {...styles.wishListCardGridItem}
                        key={wishList.id}
                        data-test-selector="cardListRecentWishLists_container"
                    >
                        <MyListsDetailsPageTypeLink
                            title={wishList.name}
                            wishListId={wishList.id}
                            testSelector="wishListCardName"
                        />
                    </GridItem>
                );
            })}
            {(recentWishListsDataView.pagination?.numberOfPages || 1) > 1 && (
                <GridItem {...styles.loadMoreGridItem} data-test-selector="cardListRecentWishLists_container">
                    <Link href={myListsPageNavLink?.url} {...styles.loadMoreLink} aria-label="Load More">
                        {translate("Load More")}
                    </Link>
                </GridItem>
            )}
        </CardList>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavWishLists),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        icon: "List",
    },
};

export default widgetModule;
